import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControlLabel,
  FormGroup,
  IconButton,
  List,
  ListItem,
  ListItemText,
  Stack,
  Switch,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Typography,
} from '@mui/material';
import FiberManualRecordIcon from '@mui/icons-material/FiberManualRecord';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import StopCircleIcon from '@mui/icons-material/StopCircle';
import { useState, type InputHTMLAttributes } from 'react';
import { OperationProgressIndicator } from '../../shared/ui/progress';
import { getWorkspaceIconButtonSx } from '../../shared/ui/workflow/WorkflowWorkspacePrimitives';
import { useRecorderStore, type RecorderPrivacyOptions } from '../recording/useRecorderStore';
import type { RecordingMode } from '../recording/scenarioRecorder';
import { buildPrivacyWarning } from '../archive/scenarioRedaction';

/** Privacy toggles rendered in the save form (spec §19). English-only labels. */
const PRIVACY_TOGGLES: ReadonlyArray<{
  key: keyof RecorderPrivacyOptions;
  label: string;
  testId: string;
}> = [
  { key: 'excludeAbsolutePaths', label: 'Remove absolute file paths', testId: 'recorder-privacy-exclude-paths' },
  { key: 'maskLocalDirectories', label: 'Mask local directories (keep file names)', testId: 'recorder-privacy-mask-dirs' },
  { key: 'maskUsername', label: 'Mask the user name', testId: 'recorder-privacy-mask-username' },
  { key: 'disableModelSave', label: 'Do not save the model', testId: 'recorder-privacy-disable-model' },
  { key: 'disableBigPayloadSave', label: 'Do not save large payloads', testId: 'recorder-privacy-disable-payloads' },
];

/**
 * User-facing Test-Records recorder controls: a record/stop toggle, an active
 * indicator, a Full/Lightweight mode selector, and (on stop) a name+description
 * form gating the archive download. All text English-only. The capture logic and
 * archive build live in other cards; this component only drives
 * {@link useRecorderStore}.
 */
type RecorderControlsProps = {
  readonly isCollapsed?: boolean;
  readonly onCollapseChange?: (isCollapsed: boolean) => void;
};

export default function RecorderControls({
  isCollapsed: controlledIsCollapsed,
  onCollapseChange,
}: RecorderControlsProps = {}) {
  const [localIsCollapsed, setLocalIsCollapsed] = useState(false);
  const isCollapsed = controlledIsCollapsed ?? localIsCollapsed;
  const setIsCollapsed = onCollapseChange ?? setLocalIsCollapsed;
  const {
    isRecording,
    mode,
    steps,
    showStopForm,
    scenarioName,
    description,
    error,
    warnings,
    setMode,
    startRecording,
    requestStop,
    cancelStop,
    setScenarioName,
    setDescription,
    canDownload,
    confirmDownload,
    dismissError,
    privacy,
    setPrivacyOption,
    downloading,
  } = useRecorderStore();

  const nameValid = canDownload();
  const recorderActionLabel = isRecording ? 'Stop scenario recording' : 'Record test scenario';
  const showDockLabel = isRecording
    ? 'Show developer tools — recording'
    : 'Show developer tools';

  if (isCollapsed) {
    return (
      <Box data-testid="recorder-controls" sx={{ m: -0.75 }}>
        <Tooltip title={showDockLabel}>
          <IconButton
            size="small"
            aria-label={showDockLabel}
            onClick={() => setIsCollapsed(false)}
            sx={(theme) => ({
              ...getWorkspaceIconButtonSx(theme, { size: 26 }),
              backgroundColor: 'transparent',
              borderColor: 'transparent',
              opacity: 0.72,
              '&:hover': { backgroundColor: theme.palette.action.hover, opacity: 1 },
              '&.Mui-focusVisible': {
                backgroundColor: theme.palette.action.selected,
                opacity: 1,
                outline: `2px solid ${theme.palette.primary.main}`,
                outlineOffset: 2,
              },
            })}
          >
            {isRecording
              ? <FiberManualRecordIcon color="error" fontSize="small" />
              : <KeyboardArrowUpIcon fontSize="small" />}
          </IconButton>
        </Tooltip>
      </Box>
    );
  }

  return (
    <Box data-testid="recorder-controls">
      {error && (
        <Alert severity="error" data-testid="recorder-error" onClose={dismissError} sx={{ mb: 1 }}>
          {error}
        </Alert>
      )}

      {warnings.length > 0 && (
        <Alert severity="warning" data-testid="recorder-warning" sx={{ mb: 1 }}>
          {warnings.length === 1
            ? warnings[0]
            : `Some data could not be collected (${warnings.length} items).`}
        </Alert>
      )}

      <Stack direction="row" spacing={1} alignItems="center">
        <ToggleButtonGroup
          exclusive
          size="small"
          value={mode}
          data-testid="recorder-mode-select"
          aria-label="Recording mode"
          onChange={(_event, value: RecordingMode | null) => {
            if (value) {
              setMode(value);
            }
          }}
        >
          <ToggleButton value="full" data-testid="recorder-mode-full" disabled={isRecording}>
            Full
          </ToggleButton>
          <ToggleButton
            value="lightweight"
            data-testid="recorder-mode-lightweight"
            disabled={isRecording}
          >
            Lightweight
          </ToggleButton>
        </ToggleButtonGroup>

        <Button
          variant="contained"
          color={isRecording ? 'error' : 'primary'}
          data-testid="recorder-toggle"
          aria-label={recorderActionLabel}
          startIcon={isRecording ? <StopCircleIcon /> : <FiberManualRecordIcon />}
          onClick={isRecording ? requestStop : startRecording}
          sx={{
            minWidth: { xs: 32, sm: 'auto' },
            px: { xs: 1, sm: 2 },
            whiteSpace: 'nowrap',
            '& .MuiButton-startIcon': {
              ml: 0,
              mr: { xs: 0, sm: 1 },
            },
          }}
        >
          <Box component="span" sx={{ display: { xs: 'none', sm: 'inline' } }}>
            {recorderActionLabel}
          </Box>
        </Button>

        {isRecording && (
          <Stack direction="row" spacing={0.5} alignItems="center" data-testid="recorder-indicator">
            <FiberManualRecordIcon color="error" fontSize="small" />
            <Typography variant="body2" color="error">
              Recording…
            </Typography>
          </Stack>
        )}

        <Tooltip title="Hide developer tools">
          <IconButton
            size="small"
            aria-label="Hide developer tools"
            onClick={() => setIsCollapsed(true)}
            sx={getWorkspaceIconButtonSx}
          >
            <KeyboardArrowDownIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      </Stack>

      <Dialog open={showStopForm} onClose={cancelStop} data-testid="recorder-stop-form">
        <DialogTitle>Save recorded scenario</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <Alert severity="warning" data-testid="recorder-privacy-warning">
              {buildPrivacyWarning(privacy)}
            </Alert>
            <Box data-testid="recorder-privacy-options">
              <Typography variant="subtitle2">Privacy options</Typography>
              <FormGroup>
                {PRIVACY_TOGGLES.map((toggle) => (
                  <FormControlLabel
                    key={toggle.key}
                    control={
                      <Switch
                        size="small"
                        checked={privacy[toggle.key]}
                        onChange={(event) => setPrivacyOption(toggle.key, event.target.checked)}
                        slotProps={{
                          input: { 'data-testid': toggle.testId } as InputHTMLAttributes<HTMLInputElement>,
                        }}
                      />
                    }
                    label={toggle.label}
                  />
                ))}
              </FormGroup>
            </Box>
            <Divider />
            <TextField
              required
              fullWidth
              label="Scenario name"
              value={scenarioName}
              error={!nameValid}
              helperText={nameValid ? ' ' : 'A scenario name is required.'}
              onChange={(event) => setScenarioName(event.target.value)}
              inputProps={{ 'data-testid': 'recorder-name-input' }}
            />
            <TextField
              fullWidth
              multiline
              minRows={2}
              label="Description (optional)"
              value={description}
              onChange={(event) => setDescription(event.target.value)}
              inputProps={{ 'data-testid': 'recorder-description-input' }}
            />
            {steps.length > 0 && (
              <Box>
                <Typography variant="subtitle2">{steps.length} recorded steps</Typography>
                <List dense data-testid="recorder-steps">
                  {steps.map((step) => (
                    <ListItem key={step.index} disableGutters>
                      <ListItemText primary={`${step.index}. [${step.type}] ${step.action}`} />
                    </ListItem>
                  ))}
                </List>
              </Box>
            )}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button data-testid="recorder-cancel" onClick={cancelStop} disabled={downloading}>
            Cancel
          </Button>
          <Button
            variant="contained"
            data-testid="recorder-download"
            disabled={!nameValid || downloading}
            startIcon={downloading ? <OperationProgressIndicator size={16} color="inherit" ariaLabel="Saving" /> : undefined}
            onClick={() => {
              void confirmDownload();
            }}
          >
            {downloading ? 'Saving…' : 'Download .zip'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
